import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { EditorContent } from "@tiptap/react"
import {
	IconArrowUpOutline18 as ArrowUp,
	IconCirclePlusOutline18 as AttachIcon,
	IconCheckOutline18 as Check,
	IconCircleCheckOutline18 as CircleCheck,
	IconCode3Outline18 as CodeXml,
	IconPenOutline18 as Edit,
	IconTrashOutline18 as Trash,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { FormattedAttachment } from "@productlane/ui"
import {
	canDeleteComment,
	canEditComment,
	cn,
	COMMENT_DELETE_FORBIDDEN,
} from "@productlane/lib/src/browser"
import {
	AlertDialog,
	AttachmentsList,
	Avatar,
	IconButton,
	useAttachments,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"

import type { TimelineEventProps } from "./types"
import type { Editor } from "@tiptap/react"
import { createCommentEditorInstance } from "@/components/editor/use-comment-editor"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { generateId } from "@/lib/id"
import { useSession } from "@/lib/session"
import { mutateOk, useZero } from "@/lib/zero"
import { SlackStyleMessage } from "./base/slack-style-message"
import { CommentBody } from "./comment-body"
import {
	COMMENT_COMPOSER_ACTION_SIZE,
	COMMENT_COMPOSER_AVATAR_SIZE,
	COMMENT_COMPOSER_INDENT_CLASS,
	commentComposerLayout,
} from "./comment-composer-layout"
import { AddReactionButton, MessageReactions } from "./message-reactions"

export function CommentEvent({
	event,
	showAvatar = true,
	isReply = false,
}: TimelineEventProps & { isReply?: boolean }) {
	const { session } = useSession()
	const zero = useZero()
	const commentUserId = event.type === "comment" ? event.comment.userId : null
	const isUserCommentOwner = canEditComment({
		commentUserId,
		actorUserId: session?.userId,
	})
	const isCommentDeletable = canDeleteComment({
		commentUserId,
		actorUserId: session?.userId,
		role: session?.membershipRole,
	})
	const canResolve = Boolean(session) && session?.membershipRole !== "VIEWER"
	const isResolved = event.type === "comment" && event.comment.isResolved
	const [editor, setEditor] = useState<Editor | null>(null)
	const [isDeleteCommentOpen, setDeleteCommentOpen] = useToggle(false)

	// Destroy editor when it changes or component unmounts
	useEffect(() => {
		return () => {
			editor?.destroy()
		}
	}, [editor])

	const handleEditComment = useCallback(() => {
		if (event.type !== "comment") return
		const ed = createCommentEditorInstance({
			initialContent: event.comment.content,
		})
		setEditor(ed)
	}, [event])

	const handleDeleteComment = useCallback(() => {
		setDeleteCommentOpen(true)
	}, [setDeleteCommentOpen])

	const abortEditComment = useCallback(() => {
		setEditor(null)
	}, [])

	const updateComment = useCallback(async () => {
		if (event.type === "comment" && editor && !editor.isEmpty) {
			const html = editor.getHTML()
			setEditor(null)
			const result = await zero.mutate(
				mutators.comments.update({
					id: event.comment.id,
					content: html,
				}),
			).client
			if (result.type === "success") {
				toast.success({ title: "Comment updated!" })
			} else {
				toast.error({ title: "Failed to update comment." })
			}
		} else {
			toast.error({ title: "Comment can't be empty." })
		}
	}, [zero, editor, event])

	const deleteComment = useCallback(async () => {
		if (event.type === "comment") {
			const result = await zero.mutate(
				mutators.comments.delete({
					id: event.comment.id,
				}),
			).client
			if (result.type === "success") {
				toast.success({ title: "Comment deleted!" })
			} else {
				toast.error({
					title:
						result.error.message === COMMENT_DELETE_FORBIDDEN
							? COMMENT_DELETE_FORBIDDEN
							: "Failed to delete comment.",
					icon: Trash,
				})
			}
			setDeleteCommentOpen(false)
		}
	}, [zero, event, setDeleteCommentOpen])

	const toggleResolved = useCallback(async () => {
		if (event.type !== "comment") return
		const nextResolved = !event.comment.isResolved
		const result = await zero.mutate(
			mutators.comments.setResolved({
				id: event.comment.id,
				isResolved: nextResolved,
			}),
		).client
		if (result.type !== "success") {
			toast.error({
				title: nextResolved
					? "Couldn't resolve this comment thread."
					: "Couldn't reopen this comment thread.",
				icon: CircleCheck,
			})
		}
	}, [zero, event])

	useShortcut(
		"meta+enter, ctrl+enter",
		// eslint-disable-next-line @typescript-eslint/no-misused-promises
		async () => {
			if (editor?.isEditable && editor.isFocused) {
				await updateComment()
			}
		},
		{
			enableOnContentEditable: true,
		},
	)
	useShortcut(
		"meta+esc, ctrl+esc",
		() => {
			if (editor?.isEditable && editor.isFocused) {
				abortEditComment()
			}
		},
		{
			enableOnContentEditable: true,
		},
	)

	const sortedAttachments = useMemo(() => {
		if (event.type !== "comment") return []
		return (event?.comment?.attachments ?? [])
			.map((attachment) => {
				return (
					typeof attachment === "string" ? JSON.parse(attachment) : attachment
				) as FormattedAttachment
			})
			.sort((a, b) => {
				if (a.ContentType.includes("video")) return -1
				if (b.ContentType.includes("video")) return 1
				if (a.ContentType.includes("image")) return -1
				if (b.ContentType.includes("image")) return 1
				return 0
			})
	}, [event])

	if (event.type !== "comment") return null

	const replies = event.replies ?? []
	const replyCount = replies.length
	const authorName = event.comment.userName ?? "Unknown user"

	const hasMenuItems =
		(!isReply && canResolve) || isUserCommentOwner || isCommentDeletable

	const actionMenu = (
		<DropdownMenu
			trigger={
				<IconButton
					Icon={MoreHorizontal}
					variant="ghost"
					size="sm"
					iconClassName="fill-current"
				/>
			}
			contentProps={{ side: "bottom", align: "end" }}
		>
			<DropdownMenuGroup>
				{!isReply && canResolve && (
					<DropdownMenuItem Icon={CircleCheck} onClick={toggleResolved}>
						{isResolved ? "Reopen thread" : "Resolve thread"}
					</DropdownMenuItem>
				)}
				{isUserCommentOwner && (
					<DropdownMenuItem Icon={Edit} onClick={handleEditComment}>
						Edit
					</DropdownMenuItem>
				)}
				{isCommentDeletable && (
					<DropdownMenuItem Icon={Trash} onClick={handleDeleteComment}>
						Delete
					</DropdownMenuItem>
				)}
			</DropdownMenuGroup>
		</DropdownMenu>
	)

	const deleteDialog = isCommentDeletable ? (
		<AlertDialog
			isOpen={isDeleteCommentOpen}
			onOpenChange={setDeleteCommentOpen}
			confirmText="Delete"
			variant="positive"
			title={
				isUserCommentOwner
					? "Delete this comment?"
					: `Delete ${authorName}'s comment?`
			}
			description={
				replyCount > 0
					? `Deleting this comment also deletes its ${replyCount === 1 ? "reply" : `${replyCount} replies`}.`
					: "The comment is removed from this thread for everyone."
			}
			onCancel={() => {
				setDeleteCommentOpen(false)
			}}
			onConfirm={deleteComment}
		/>
	) : null

	const rowContent = (
		<div className="group/reactable group">
			{deleteDialog}
			<SlackStyleMessage
				showAvatar={showAvatar}
				senderName={authorName}
				senderImage={event.comment.userImage}
				senderUserId={commentUserId}
				avatarSize="s"
				avatarContent={
					!event.comment.userName && !event.comment.userImage ? (
						<div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 dark:bg-gray-850">
							<CodeXml className="text-gray-500 dark:text-gray-400" />
						</div>
					) : undefined
				}
				timestamp={new Date(event.comment.createdAt)}
				variant="comment"
				bareComment
				indentContent={isReply}
				actions={
					<div className="flex items-center gap-0.5">
						<AddReactionButton
							threadId={event.comment.feedbackId}
							messageType="comment"
							messageId={event.comment.id}
						/>
						{hasMenuItems ? (
							<div className="flex items-center opacity-0 focus-within:opacity-100 group-hover:opacity-100 has-[[data-state=open]]:opacity-100">
								{actionMenu}
							</div>
						) : null}
					</div>
				}
				footer={
					<MessageReactions
						threadId={event.comment.feedbackId}
						messageType="comment"
						messageId={event.comment.id}
					/>
				}
			>
				<div>
					{editor ? (
						<>
							<EditorContent editor={editor} />
							{isUserCommentOwner && (
								<div className="mt-2 flex justify-end gap-1.5">
									<IconButton
										variant="ghost"
										Icon={X}
										onClick={abortEditComment}
									/>
									<IconButton Icon={Check} onClick={updateComment} />
								</div>
							)}
						</>
					) : (
						<CommentBody
							html={event.comment.content}
							fromLinear={Boolean(event.comment.linearAuthorId)}
						/>
					)}
				</div>
				{sortedAttachments && sortedAttachments.length > 0 && (
					<>
						{event.comment.content && event.comment.content !== "<p></p>" && (
							<hr className="my-3" />
						)}
						<AttachmentsList
							enableViewer
							showPointer={false}
							attachments={sortedAttachments.map((attachment) => ({
								...attachment,
								Content: attachment.Content ?? "",
							}))}
						/>
					</>
				)}
			</SlackStyleMessage>
		</div>
	)

	if (isReply) return rowContent

	return (
		<div className="py-1">
			<div className="-mx-2 overflow-hidden rounded-2xl border border-[#F2E6C9] bg-[#FFFBF2] dark:border-[#3D331C] dark:bg-[#221D12]">
				{rowContent}
				{replies.map((reply) => (
					<div
						key={reply.id}
						className="border-t border-[#F2E6C9] dark:border-[#3D331C]"
					>
						<CommentEvent
							event={{ type: "comment", comment: reply, replies: [] }}
							isReply
							showAvatar
						/>
					</div>
				))}
				<div className="border-t border-[#F2E6C9] dark:border-[#3D331C]">
					<ReplyComposer
						feedbackId={event.comment.feedbackId}
						parentId={event.comment.id}
					/>
				</div>
			</div>
		</div>
	)
}

function ReplyComposer({
	feedbackId,
	parentId,
}: {
	feedbackId: string
	parentId: string
}) {
	const { session } = useSession()
	const zero = useZero()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const getSignedUrlMutation = api.file.getUrl.useMutation()
	const deleteFileUrlMutation = api.file.deleteFileUrl.useMutation()

	const {
		attachments,
		formattedAttachments,
		addAttachments,
		removeAttachment,
		clearAttachments,
		isAttachmentsLoading,
	} = useAttachments({
		contextId: `reply-${parentId}`,
		getSignedUrl: getSignedUrlMutation.mutateAsync,
		deleteFileUrl: async ({ fileUrl }) => {
			await deleteFileUrlMutation.mutateAsync({ fileUrl })
		},
		errorHandler: ({ title, description }) => {
			toast.error({ title, description })
		},
	})

	const [editor] = useState<Editor>(() =>
		createCommentEditorInstance({
			placeholder: "Leave a reply...",
			autofocus: false,
		}),
	)

	useEffect(() => {
		return () => {
			editor.destroy()
		}
	}, [editor])

	const submit = useCallback(async () => {
		if (!session) return
		if (editor.isEmpty && formattedAttachments.length === 0) return
		const html = editor.getHTML()
		const attachmentsPayload = formattedAttachments.map((a) =>
			JSON.stringify(a),
		)
		editor.commands.clearContent()
		clearAttachments()
		await mutateOk(
			zero.mutate(
				mutators.comments.create({
					id: generateId(),
					content: html,
					feedbackId,
					userId: session.userId,
					parentId,
					attachments: attachmentsPayload,
				}),
			),
		)
	}, [
		editor,
		session,
		zero,
		feedbackId,
		parentId,
		formattedAttachments,
		clearAttachments,
	])

	useShortcut(
		"meta+enter, ctrl+enter",
		// eslint-disable-next-line @typescript-eslint/no-misused-promises
		async () => {
			if (editor.isFocused) await submit()
		},
		{ enableOnContentEditable: true },
	)

	return (
		<div
			role="presentation"
			className={cn("cursor-text", commentComposerLayout.container)}
			onClick={() => editor.commands.focus()}
			onKeyDown={(e) => {
				if (
					e.target === e.currentTarget &&
					(e.key === "Enter" || e.key === " ")
				) {
					e.preventDefault()
					editor.commands.focus()
				}
			}}
		>
			<input
				ref={fileInputRef}
				type="file"
				multiple
				className="hidden"
				onChange={(e) => {
					if (e.target.files?.length) void addAttachments(e.target.files)
					e.target.value = ""
				}}
			/>
			{attachments.length > 0 && (
				<div
					className={cn(
						"mb-2 flex flex-wrap gap-1.5",
						COMMENT_COMPOSER_INDENT_CLASS,
					)}
				>
					{attachments.map((a) => (
						<div
							key={a.id}
							className="flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
						>
							<span className="max-w-40 truncate">{a.name}</span>
							{a.state === "loading" ? (
								<span className="text-gray-400">…</span>
							) : (
								<button
									type="button"
									className="cursor-default text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
									onClick={(e) => {
										e.stopPropagation()
										void removeAttachment(a.id)
									}}
								>
									<X className="h-3 w-3" />
								</button>
							)}
						</div>
					))}
				</div>
			)}
			<div className={commentComposerLayout.row}>
				<div className={commentComposerLayout.avatar}>
					<Avatar
						size={COMMENT_COMPOSER_AVATAR_SIZE}
						roundness="full"
						name={session?.name ?? ""}
						image={session?.imageUrl}
					/>
				</div>
				<div className={commentComposerLayout.editor}>
					<EditorContent editor={editor} className="[&>.prose]:max-w-full" />
				</div>
				<div className={commentComposerLayout.actions}>
					<IconButton
						variant="ghost"
						size={COMMENT_COMPOSER_ACTION_SIZE}
						Icon={AttachIcon}
						disabled={isAttachmentsLoading}
						onClick={(e) => {
							e.stopPropagation()
							fileInputRef.current?.click()
						}}
					/>
					<IconButton
						variant="ghost"
						size={COMMENT_COMPOSER_ACTION_SIZE}
						Icon={ArrowUp}
						onClick={submit}
					/>
				</div>
			</div>
		</div>
	)
}
